// Blog article: sticky header offset, TOC navigation & active chapter tracking
class BlogNewUi extends HTMLElement {
  connectedCallback() {
    this.observeStickyHeader();

    this.tocLinks = [...this.querySelectorAll('[data-toc-link]')];
    if (!this.tocLinks.length) return;

    this.chapters = this.tocLinks
      .map(({ dataset }) => document.getElementById(dataset.target))
      .filter(Boolean);

    this.scrollBehavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';

    this.addEventListener('click', this.handleTocClick);
    this.observeActiveChapter();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleTocClick);
    window.removeEventListener('scroll', this.requestHeaderOffsetUpdate);
    this.stickyHeader?.removeEventListener('transitionend', this.updateHeaderOffset);
    this.headerResizeObserver?.disconnect();
    this.chapterObserver?.disconnect();
  }

  // Sticky header: expose its visible bottom edge & full height as CSS variables
  observeStickyHeader() {
    const { headerSelector } = this.dataset;
    this.stickyHeader = headerSelector ? document.querySelector(headerSelector) : null;
    if (!this.stickyHeader) return;

    this.headerResizeObserver = new ResizeObserver(this.updateHeaderOffset);
    this.headerResizeObserver.observe(this.stickyHeader);
    this.stickyHeader.addEventListener('transitionend', this.updateHeaderOffset);
    window.addEventListener('scroll', this.requestHeaderOffsetUpdate, { passive: true });
    this.updateHeaderOffset();
  }

  requestHeaderOffsetUpdate = () => {
    if (this.headerOffsetFrame) return;
    this.headerOffsetFrame = requestAnimationFrame(() => {
      this.headerOffsetFrame = null;
      this.updateHeaderOffset();
    });
  };

  updateHeaderOffset = () => {
    const { bottom, height } = this.stickyHeader.getBoundingClientRect();
    this.style.setProperty('--bnu-header-visible', `${Math.max(0, Math.round(bottom))}px`);
    this.style.setProperty('--bnu-header-height', `${Math.round(height)}px`);
  };

  // TOC: smooth scroll to chapter
  handleTocClick = (event) => {
    const link = event.target.closest('[data-toc-link]');
    if (!link) return;

    const { target } = link.dataset;
    const chapter = document.getElementById(target);
    if (!chapter) return;

    event.preventDefault();
    chapter.scrollIntoView({ behavior: this.scrollBehavior, block: 'start' });
    history.replaceState(null, '', `#${target}`);
    this.setActiveTocLink(target);
  };

  // Scroll-spy: highlight the chapter crossing the upper part of the viewport
  observeActiveChapter() {
    this.chapterObserver = new IntersectionObserver(
      (entries) => {
        entries
          .filter(({ isIntersecting }) => isIntersecting)
          .forEach(({ target }) => this.setActiveTocLink(target.id));
      },
      { rootMargin: '-25% 0px -65% 0px' }
    );

    this.chapters.forEach((chapter) => this.chapterObserver.observe(chapter));
  }

  setActiveTocLink(chapterId) {
    this.tocLinks.forEach((link) => {
      const isActive = link.dataset.target === chapterId;
      link.classList.toggle('is-active', isActive);
      isActive ? link.setAttribute('aria-current', 'true') : link.removeAttribute('aria-current');
    });
  }
}

if (!customElements.get('blog-new-ui')) {
  customElements.define('blog-new-ui', BlogNewUi);
}